import { v4 as uuidv4 } from "uuid";

import { API_CONFIG } from "../config/apiConfig";
import { getCurrentClient, hasValidSubdomain } from "../utils/subdomainUtils";
import { smartApiRouter, getApiUrl, getCachingHeaders } from "./smartApiRouter";
import { getAccessToken, login } from "../auth/oidcConfig";

interface ApiErrorDetail {
  status?: number;
  code?: string;
  title?: string;
  detail?: string;
  error_code?: string;
}

export interface ResponseData {
  success: boolean;
  data?: unknown;
  meta?: unknown;
  error?: string;
  errorCode?: string;
  errors?: ApiErrorDetail[];
}

interface FetchOptions {
  url: string;
  method: string;
  requestData?: unknown;
  isJson?: boolean;
  responseDefaults?: Record<string, unknown>;
  showModalOnError?: boolean;
  isSendingFile?: boolean;
  customHeaders?: Record<string, string>;
  signal?: AbortSignal;
}

interface ApiError extends Error {
  status: number;
  message: string;
}

type HeaderAttributes = Record<string, string>;

export abstract class BaseApiService {
  protected baseUrl: string;
  protected jwtToken: string | null;
  protected clientId: string | null;
  public static sessionId: string = uuidv4();

  constructor() {
    // Use API_CONFIG which handles multi-tenant URL building
    // Note: baseUrl will be dynamically determined by smartApiRouter
    this.baseUrl = API_CONFIG.app.apiUrl;

    // JWT Token from environment variable (optional)
    this.jwtToken = import.meta.env.VITE_JWT_TOKEN || null;

    // Multi-tenant client ID
    this.clientId = getCurrentClient();
  }

  // Get the client ID (for multi-tenant access)
  getClientId(): string | null {
    return this.clientId;
  }

  // Check if subdomain is valid
  hasValidSubdomain(): boolean {
    return hasValidSubdomain();
  }

  // Set JWT token (for dynamic authentication)
  setJwtToken(token: string | null): void {
    this.jwtToken = token;
  }

  // Get current JWT token
  getJwtToken(): string | null {
    return this.jwtToken;
  }

  // Check if user is authenticated
  isAuthenticated(): boolean {
    return this.jwtToken !== null && this.jwtToken.trim() !== "";
  }

  // Clear JWT token (for logout)
  clearJwtToken(): void {
    this.jwtToken = null;
  }

  static getHeaders(
    _url: string,
    isSendingFile: boolean,
    customHeaders?: Record<string, string>
  ): HeaderAttributes {
    const headers: HeaderAttributes = {
      Accept: "application/json",
      ...customHeaders,
    };

    if (this.sessionId) {
      headers["sessionid"] = this.sessionId;
    }

    if (!isSendingFile) {
      headers["Content-Type"] = "application/json;charset=UTF-8";
    }

    return headers;
  }

  protected getHeaders(): Record<string, string> {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };

    // Only add Authorization header if JWT token is available
    if (this.jwtToken) {
      headers["Authorization"] = `Bearer ${this.jwtToken}`;
    }

    // Add client header (always required)
    if (this.clientId) {
      headers["X-Client-ID"] = this.clientId;
    }

    return headers;
  }

  static async doFetch({
    url,
    method,
    requestData = null,
    isJson = true,
    responseDefaults = {},
    showModalOnError = true,
    isSendingFile = false,
    customHeaders = {},
    signal,
  }: FetchOptions): Promise<ResponseData> {
    const formattedRequestData = isSendingFile
      ? requestData
      : JSON.stringify(requestData);

    // 🔄 FIX RACE CONDITION: Check OIDC token BEFORE determining API endpoint
    // This prevents race condition where OIDC updates but smartApiRouter has stale cache
    // Get OIDC token if user is authenticated
    // Note: We're using ID token instead of access token because the access token is opaque.
    // The backend validates the JWT (ID token) which contains user claims.
    const accessToken = await getAccessToken();

    // If we have a token but smartApiRouter thinks we're not authenticated, refresh it
    const currentAuthState = smartApiRouter.getCurrentAuthState();
    if (accessToken && !currentAuthState?.isAuthenticated) {
      console.log(
        "🔄 Token found but router thinks user is not authenticated - refreshing router state..."
      );
      await smartApiRouter.refreshAuthState();
    }

    // Use smart API router to determine the correct API endpoint
    const fullUrl = await getApiUrl(url);

    // Get appropriate caching headers based on auth state
    const cachingHeaders = await getCachingHeaders();

    // Log API mode for debugging
    const authState = smartApiRouter.getCurrentAuthState();
    const apiMode = authState?.isAuthenticated ? "private" : "public";
    console.log(
      `🔄 API Call: ${method} ${url} (${apiMode} mode) -> ${fullUrl}`
    );

    // Build headers with OIDC token
    const headers: Record<string, string> = {
      ...this.getHeaders(url, isSendingFile, customHeaders),
      ...cachingHeaders,
    };

    // Add Authorization header with OIDC token if available
    if (accessToken) {
      headers["Authorization"] = `Bearer ${accessToken}`;
      console.log("🔐 Using OIDC access token for authentication");
    }

    try {
      const response = await fetch(fullUrl, {
        method,
        credentials: "include",
        headers,
        body: requestData ? (formattedRequestData as BodyInit) : null,
        signal,
      });

      if (!response.ok) {
        throw response;
      }

      const data = isJson ? await response.json() : await response.text();
      return { success: true, ...data };
    } catch (error) {
      // Preserve abort semantics for upstream detection
      if (error instanceof DOMException && error.name === "AbortError") {
        throw error;
      }
      const apiError = error as ApiError;
      if (showModalOnError) {
        console.error("API Error:", apiError);
      }
      if (apiError.status) {
        let errorMessage = "An error occurred";
        let errorCode = "";

        try {
          if (error instanceof Response) {
            const errorData = await error.json().catch(() => ({}));

            // Handle new error format with errors array
            if (
              errorData.errors &&
              Array.isArray(errorData.errors) &&
              errorData.errors.length > 0
            ) {
              const firstError = errorData.errors[0];
              errorMessage =
                firstError.title ||
                firstError.detail ||
                `HTTP ${error.status}: ${error.statusText}`;
              errorCode = firstError.error_code || "";

              return {
                success: false,
                error: errorMessage,
                errorCode,
                errors: errorData.errors,
                data: responseDefaults,
              };
            } else {
              // Handle old error format and new structured errors
              errorMessage =
                errorData.message ||
                errorData.error ||
                errorData.errors ||
                `HTTP ${error.status}: ${error.statusText}`;
              errorCode = errorData.errorCode || errorData.error || "";

              // Handle PUBLIC_PORTAL_DISABLED error - redirect to login
              if (errorData.error === "PUBLIC_PORTAL_DISABLED") {
                console.log(
                  "🔒 Public portal disabled, redirecting to login..."
                );
                this.redirectToLogin();
                return {
                  success: false,
                  error: errorMessage,
                  errorCode,
                  data: responseDefaults,
                };
              }
            }
          }
        } catch {
          errorMessage = `HTTP ${apiError.status}: Unknown error`;
        }

        return {
          success: false,
          error: errorMessage,
          errorCode,
          data: responseDefaults,
        };
      }

      return {
        success: false,
        error: apiError.message || "An error occurred",
        data: responseDefaults,
      };
    }
  }

  static get({
    url,
    responseDefaults,
    showModalOnError,
    customHeaders,
    signal,
  }: {
    url: string;
    responseDefaults?: Record<string, unknown>;
    showModalOnError?: boolean;
    customHeaders?: Record<string, string>;
    signal?: AbortSignal;
  }): Promise<ResponseData> {
    return this.doFetch({
      url,
      method: "GET",
      responseDefaults,
      showModalOnError,
      customHeaders,
      signal,
    });
  }

  static delete({
    url,
    responseDefaults,
    customHeaders,
  }: {
    url: string;
    responseDefaults?: Record<string, unknown>;
    customHeaders?: Record<string, string>;
  }): Promise<ResponseData> {
    return this.doFetch({
      url,
      method: "DELETE",
      responseDefaults,
      customHeaders,
    });
  }

  static async post({
    url,
    requestData,
    isJson,
    responseDefaults,
    showModalOnError,
    isSendingFile,
    customHeaders,
  }: {
    url: string;
    requestData?: unknown;
    isJson?: boolean;
    responseDefaults?: Record<string, unknown>;
    showModalOnError?: boolean;
    isSendingFile?: boolean;
    customHeaders?: Record<string, string>;
  }): Promise<ResponseData> {
    return this.doFetch({
      url,
      method: "POST",
      requestData,
      isJson,
      responseDefaults,
      showModalOnError,
      isSendingFile,
      customHeaders,
    });
  }

  static async put({
    url,
    requestData,
    isJson,
    responseDefaults,
    showModalOnError = true,
    customHeaders,
  }: {
    url: string;
    requestData?: unknown;
    isJson?: boolean;
    responseDefaults?: Record<string, unknown>;
    showModalOnError?: boolean;
    customHeaders?: Record<string, string>;
  }): Promise<ResponseData> {
    return this.doFetch({
      url,
      method: "PUT",
      requestData,
      isJson,
      responseDefaults,
      showModalOnError,
      customHeaders,
    });
  }

  static async patch({
    url,
    requestData,
    responseDefaults,
    customHeaders,
  }: {
    url: string;
    requestData?: unknown;
    responseDefaults?: Record<string, unknown>;
    customHeaders?: Record<string, string>;
  }): Promise<ResponseData> {
    return this.doFetch({
      url,
      method: "PATCH",
      requestData,
      responseDefaults,
      customHeaders,
    });
  }

  // Enhanced fetch method using OIDC token for authentication
  protected async fetchWithAuth(
    requestInfo: RequestInfo,
    requestOptions?: RequestInit
  ): Promise<Response> {
    const options = this.getFetchOptions(requestOptions);

    // Get OIDC access token and add to headers
    const accessToken = await getAccessToken();
    if (accessToken) {
      options.headers = {
        ...options.headers,
        Authorization: `Bearer ${accessToken}`,
      };
    }

    return fetch(requestInfo, options);
  }

  // Get fetch options with headers and credentials
  protected getFetchOptions(options: RequestInit = {}): RequestInit {
    return {
      ...options,
      headers: {
        ...this.getHeaders(),
        ...(options.headers || {}),
      },
      credentials: "include",
    };
  }

  protected async handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `API Error: ${response.status} ${response.statusText} - ${errorText}`
      );
    }
    return await response.json();
  }

  /**
   * Redirect to OIDC login page when public portal is disabled
   */
  private static redirectToLogin(): void {
    try {
      // Get current client from subdomain
      const currentClient = getCurrentClient();

      if (!currentClient) {
        console.error("❌ Cannot redirect to login - no valid subdomain found");
        return;
      }

      console.log(`🔗 Redirecting to OIDC login...`);

      // Use OIDC login function
      login().catch((error) => {
        console.error("❌ OIDC login failed:", error);
        alert("Public access is not available. Please log in to continue.");
      });
    } catch (error) {
      console.error("❌ Error redirecting to login:", error);

      // Fallback: show alert and reload page
      alert("Public access is not available. Please log in to continue.");
      window.location.reload();
    }
  }
}
